// 共享详情浮层 / Shared detail modal.
// 提供中心详情浮层、脏状态确认和背景模糊开关。
// Provides the centered detail modal, dirty-state confirmation, and backdrop blur state.

// 详情浮层打开参数 / Options for opening the detail modal.
export interface DetailModalOptions {
  title: string;
  titleMeta?: string | null;
  dirty?: boolean;
  render: () => HTMLElement;
  footer?: () => HTMLElement;
  confirmClose?: () => boolean;
  onClose?: () => void;
  onEscape?: () => void;
}

let detailDirty = false;
let detailEvents: AbortController | null = null;
let confirmClose: (() => boolean) | null = null;
let onCloseCallback: (() => void) | null = null;
let onEscapeCallback: (() => void) | null = null;
let restoreFocusTarget: HTMLElement | null = null;

/**
 * 打开详情浮层 / Open the shared detail modal.
 */
export function openDetailModal(options: DetailModalOptions): boolean {
  const existingRoot = document.getElementById("detail-modal-root");
  if (existingRoot && !existingRoot.hidden && detailDirty && confirmClose && !confirmClose()) {
    return false;
  }
  const root = detailRoot();
  restoreFocusTarget = document.activeElement instanceof HTMLElement
    ? document.activeElement
    : null;
  detailEvents?.abort();
  detailEvents = new AbortController();
  const { signal } = detailEvents;

  detailDirty = Boolean(options.dirty);
  confirmClose = options.confirmClose ?? defaultDirtyConfirm;
  onCloseCallback = options.onClose ?? null;
  onEscapeCallback = options.onEscape ?? null;

  const overlay = document.createElement("div");
  overlay.className = "detail-modal-overlay";
  overlay.setAttribute("aria-hidden", "true");

  const panel = document.createElement("section");
  panel.className = "detail-modal-panel";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-modal", "true");
  panel.setAttribute("aria-labelledby", "detail-modal-title");
  panel.dataset.dirty = String(detailDirty);

  const header = document.createElement("header");
  header.className = "detail-modal-header";
  const title = document.createElement("h2");
  title.id = "detail-modal-title";
  title.className = "detail-modal-title";
  setDetailModalTitleContent(title, options.title, options.titleMeta ?? null);
  const closeButton = document.createElement("button");
  closeButton.type = "button";
  closeButton.className = "detail-modal-close";
  closeButton.setAttribute("aria-label", "Close detail modal");
  closeButton.textContent = "×";
  closeButton.addEventListener("click", () => closeDetailModal(), { signal });
  header.append(title, closeButton);

  const body = document.createElement("div");
  body.className = "detail-modal-body";
  body.append(options.render());

  panel.append(header, body);
  if (options.footer) {
    const footer = document.createElement("footer");
    footer.className = "detail-modal-footer";
    footer.append(options.footer());
    panel.append(footer);
  }

  root.replaceChildren(overlay, panel);
  root.hidden = false;
  document.body.classList.add("detail-modal-open");
  closeButton.focus();

  document.addEventListener("keydown", handleKeydown, { signal });
  return true;
}

/**
 * 更新详情浮层标题右侧元信息 / Update the right-side title metadata.
 */
export function setDetailModalTitleMeta(meta: string | null): boolean {
  const title = document.getElementById("detail-modal-title");
  if (!(title instanceof HTMLHeadingElement)) return false;
  setDetailModalTitleContent(title, title.dataset["title"] || title.textContent || "", meta);
  return true;
}

/**
 * 关闭详情浮层；脏状态会先确认 / Close the detail modal, confirming dirty state first.
 */
export function closeDetailModal(): boolean {
  if (detailDirty && confirmClose && !confirmClose()) return false;
  const root = document.getElementById("detail-modal-root");
  if (root) {
    root.replaceChildren();
    root.hidden = true;
  }
  detailEvents?.abort();
  detailEvents = null;
  detailDirty = false;
  confirmClose = null;
  document.body.classList.remove("detail-modal-open");
  const callback = onCloseCallback;
  onCloseCallback = null;
  onEscapeCallback = null;
  restoreFocusTarget?.focus();
  restoreFocusTarget = null;
  callback?.();
  return true;
}

/**
 * 替换详情浮层内容区 / Replace the detail modal body content.
 * 服务于"先开空浮层 + Spinner → 数据返回后填内容"的流程。
 * Used by the "open empty modal + spinner → fill content after fetch" flow.
 * 浮层已关闭时返回 false，调用方可据此决定是否放弃后续填充。
 * Returns false when the modal is already closed so callers can bail out.
 */
export function replaceDetailModalBody(content: HTMLElement): boolean {
  const root = document.getElementById("detail-modal-root");
  const body = root?.querySelector<HTMLElement>(".detail-modal-body");
  if (!root || root.hidden || !body) return false;
  body.replaceChildren(content);
  return true;
}

/**
 * 替换详情浮层底部操作区 / Replace the detail modal footer content.
 * 初次打开时可以先不传 footer（或传占位），数据到位后再注入真正的操作按钮。
 * Pair with replaceDetailModalBody to inject real action buttons once data loads.
 */
export function replaceDetailModalFooter(content: HTMLElement | null): boolean {
  const root = document.getElementById("detail-modal-root");
  const panel = root?.querySelector<HTMLElement>(".detail-modal-panel");
  if (!root || root.hidden || !panel) return false;
  let footer = panel.querySelector<HTMLElement>(".detail-modal-footer");
  if (!content) {
    footer?.remove();
    return true;
  }
  if (!footer) {
    footer = document.createElement("footer");
    footer.className = "detail-modal-footer";
    panel.append(footer);
  }
  footer.replaceChildren(content);
  return true;
}

/**
 * 判断详情浮层当前是否打开 / Whether the detail modal is currently open.
 * 异步流程用来避免对已关闭的浮层做无意义填充。
 */
export function isDetailModalOpen(): boolean {
  const root = document.getElementById("detail-modal-root");
  return !!root && !root.hidden;
}

/**
 * 设置详情浮层脏状态 / Set the detail modal dirty state.
 */
export function setDetailDirty(dirty: boolean): void {
  detailDirty = dirty;
  const panel = document.querySelector<HTMLElement>("#detail-modal-root .detail-modal-panel");
  if (panel) panel.dataset.dirty = String(detailDirty);
}

/**
 * 查询详情浮层脏状态 / Return whether the detail modal is dirty.
 */
export function isDetailDirty(): boolean {
  return detailDirty;
}

// Escape 只转发给可选回调，不关闭浮层 / Escape only forwards to an optional callback.
function handleKeydown(event: KeyboardEvent): void {
  if (event.key !== "Escape") return;
  onEscapeCallback?.();
}

// 默认脏关闭确认 / Default dirty-close confirmation.
function defaultDirtyConfirm(): boolean {
  return window.confirm("Discard unsaved changes?");
}

// 获取挂载点 / Resolve the modal root.
function detailRoot(): HTMLElement {
  const root = document.getElementById("detail-modal-root");
  if (!root) throw new Error("#detail-modal-root mount missing");
  return root;
}

// 渲染标题与右侧元信息 / Render title text and right-side metadata.
function setDetailModalTitleContent(title: HTMLElement, text: string, meta: string | null): void {
  title.dataset["title"] = text;
  const label = document.createElement("span");
  label.className = "detail-modal-title-label";
  label.textContent = text;
  title.replaceChildren(label);
  if (!meta) return;
  const metaNode = document.createElement("span");
  metaNode.className = "detail-modal-title-meta";
  metaNode.textContent = meta;
  metaNode.title = meta;
  title.append(metaNode);
}
